/* eslint-disable no-console */
/**
 * Centralized Logging Utility for Frontend
 *
 * Environment-aware logging that:
 * - Logs to console in development
 * - Suppresses debug/info logs in production
 * - Always logs warnings and errors
 * - Provides consistent formatting
 */

export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
  NONE = 4,
}

class Logger {
  private minLevel: LogLevel;
  private isDevelopment: boolean;

  private resolveIsDevelopment(): boolean {
    const processEnv = (globalThis as { process?: { env?: Record<string, string | undefined> } })
      .process?.env;

    const mode = processEnv?.MODE || processEnv?.NODE_ENV;
    if (mode) {
      return mode !== 'production';
    }

    if (processEnv?.DEV) {
      return processEnv.DEV === 'true';
    }

    if (typeof window !== 'undefined') {
      const host = window.location.hostname;
      return host === 'localhost' || host === '127.0.0.1';
    }

    return true;
  }

  constructor() {
    this.isDevelopment = this.resolveIsDevelopment();
    this.minLevel = this.isDevelopment ? LogLevel.DEBUG : LogLevel.WARN;
  }

  /**
   * Log debug information (development only)
   */
  debug(message: string, ...args: any[]): void {
    if (this.minLevel <= LogLevel.DEBUG) {
      console.log(`[DEBUG] ${message}`, ...args);
    }
  }

  /**
   * Log informational messages (development only)
   */
  info(message: string, ...args: any[]): void {
    if (this.minLevel <= LogLevel.INFO) {
      console.info(`[INFO] ${message}`, ...args);
    }
  }

  /**
   * Log warnings (always logged)
   */
  warn(message: string, ...args: any[]): void {
    if (this.minLevel <= LogLevel.WARN) {
      console.warn(`[WARN] ${message}`, ...args);
    }
  }

  /**
   * Log errors (always logged)
   */
  error(message: string, error?: any, ...args: any[]): void {
    if (this.minLevel <= LogLevel.ERROR) {
      if (error) {
        console.error(`[ERROR] ${message}`, error, ...args);
      } else {
        console.error(`[ERROR] ${message}`, ...args);
      }
    }
  }

  /**
   * Log API calls (development only)
   */
  api(method: string, endpoint: string, ...args: any[]): void {
    if (this.minLevel <= LogLevel.DEBUG) {
      console.log(`[API] ${method} ${endpoint}`, ...args);
    }
  }

  /**
   * Log cache operations (development only)
   */
  cache(operation: string, key: string, ...args: any[]): void {
    if (this.minLevel <= LogLevel.DEBUG) {
      console.log(`[CACHE] ${operation} - ${key}`, ...args);
    }
  }

  /**
   * Log search operations (development only)
   */
  search(query: string, results: number, time: number): void {
    if (this.minLevel <= LogLevel.DEBUG) {
      console.log(`[SEARCH] "${query}" - ${results} results in ${time}ms`);
    }
  }

  /**
   * Set minimum log level
   */
  setLevel(level: LogLevel): void {
    this.minLevel = level;
  }

  /**
   * Check if a log level will be output
   */
  isLevelEnabled(level: LogLevel): boolean {
    return this.minLevel <= level;
  }
}

// Export singleton instance
export const logger = new Logger();

// Export default
export default logger;
